// src/admin/api/usersApi.js
//
// User administration API. Same module shape as admin/api/rolesApi.js and
// admin/api/permissionsApi.js, mirroring
// presentation/shared/access_provisioning/user_controller.py:
//
//   GET    /api/users              → {items, total, page, page_size, total_pages}
//   POST   /api/users              → {username, email, is_active, role_ids?}
//   PUT    /api/users/{id}         → partial; role_ids REPLACES, clear_roles removes all
//   PUT    /api/users/{id}/activate→ re-activate, returns the user
//   DELETE /api/users/{id}         → deactivate, 204 NO BODY
//
// There is no password anywhere: identity comes from Google OAuth, so an
// admin pre-creates the record and its roles, and the person's first login is
// matched to it by email (see CreateUserRequest's docstring).
//
// api/usersApi.js keeps its server-paginated list() — components/UserPicker.jsx
// uses it to look a colleague up. This module is the whole-list + writes
// surface the admin page needs; the two don't overlap.

import { api } from "../../api/client";
import { toAdminError } from "./adminApiError";
import { fetchAllPages } from "./fetchAllPages";
import { listRoles } from "./rolesApi";

// snake_case → camelCase here and nowhere else. `roles` is a list of nested
// {id, name, level, permissions} objects on UserResponse — not bare names,
// unlike RoleResponse.permissions.
function toUser(dto) {
  return {
    id: dto.id,
    username: dto.username,
    email: dto.email,
    isActive: Boolean(dto.is_active),
    orgId: dto.org_id ?? null,
    roles: (dto.roles || []).map((r) => ({
      id: r.id,
      name: r.name,
      level: r.level,
    })),
    // Whether this user holds a PLATFORM-level role. Deactivating such a user
    // requires admin:grant_platform_role — the backend enforces it via
    // SetUserActivationCommand.caller_is_platform_tier, and the page hides
    // the action rather than letting it 403.
    isPlatformTier: (dto.roles || []).some((r) => r.level === "platform"),
  };
}

/** Every user in the caller's org, across all pages. */
export async function listUsers() {
  try {
    return (await fetchAllPages("/users")).map(toUser);
  } catch (err) {
    throw toAdminError(err, "Failed to load users.");
  }
}

/**
 * The roles this form may assign through the generic `role_ids` field.
 *
 * Only PLATFORM and ORG level: CreateUserHandler/UpdateUserHandler's
 * _ASSIGNABLE_LEVELS guard rejects account- and project-level roles here,
 * because those attach through the account's or project's own role-grant
 * endpoint (AccountRoleBindingORM / EngagementTeamMemberORM). Filtering by
 * level server-side means the picker never offers a choice that would fail.
 *
 * Platform roles are only fetched for a caller who can actually grant one,
 * and inactive roles are dropped — assigning a deactivated role grants
 * nothing.
 */
export async function listAssignableRoles({ includePlatform }) {
  try {
    const levels = includePlatform ? ["org", "platform"] : ["org"];
    const results = await Promise.all(levels.map((level) => listRoles({ level })));
    return results.flat().filter((r) => r.isActive);
  } catch (err) {
    throw toAdminError(err, "Failed to load assignable roles.");
  }
}

export async function createUser({ username, email, roleIds }) {
  try {
    const payload = { username: username.trim(), email: email.trim() };
    // Omitted rather than sent empty: role_ids is Optional, and an empty list
    // means the same thing as absent on create.
    if (roleIds.length > 0) payload.role_ids = roleIds;

    const res = await api.post("/users", payload);
    return toUser(res.data);
  } catch (err) {
    throw toAdminError(err, "Could not create the user.", "email");
  }
}

/**
 * Partial update. `role_ids` REPLACES the user's whole set, and omitting it
 * means "leave unchanged" — so removing every role can only be expressed as
 * `clear_roles: true` (see UpdateUserRequest's docstring). That asymmetry is
 * why an empty selection is translated rather than just sent.
 */
export async function updateUser(id, { username, email, roleIds }) {
  try {
    const payload = { username: username.trim(), email: email.trim() };
    if (roleIds.length > 0) payload.role_ids = roleIds;
    else payload.clear_roles = true;

    const res = await api.put(`/users/${id}`, payload);
    return toUser(res.data);
  } catch (err) {
    throw toAdminError(err, "Could not update the user.", "email");
  }
}

/** Re-activate so the person can sign in again. Idempotent; returns the user. */
export async function activateUser(id) {
  try {
    const res = await api.put(`/users/${id}/activate`);
    return toUser(res.data);
  } catch (err) {
    throw toAdminError(err, "Could not activate the user.");
  }
}

/**
 * Deactivate (soft — the row is kept, is_active goes false).
 *
 * Returns nothing: the route is 204 No Content, so there is no body to map.
 * The caller refetches rather than patching a returned record in place.
 */
export async function deactivateUser(id) {
  try {
    await api.delete(`/users/${id}`);
  } catch (err) {
    throw toAdminError(err, "Could not deactivate the user.");
  }
}
